import React, { useState, useEffect } from "react";
import tw from "tailwind-styled-components";
import { MdClose } from "react-icons/md";
import { FaBolt, FaBroom, FaRegCalendar } from "react-icons/fa";
import { MdGasMeter, MdOutlineWifi } from "react-icons/md";
import { useMutation } from "react-query";
import axios from "axios";
import { CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import UtilityBillSelectionScreen from "./UtilityBillSelectionScreen";
import UtilityBillForm from "./UtilityBillForm";
import UtilityBillHeader from "./UtilityBillHeader";
import AddElectricityBill from "../AddElectricityBill";
import Config from "../../../Config";
import { ModalHeader, ModalTitle } from "../../Styles/InputStyles";

const SelectUtilityBill = ({
    setIsUtilityBill,
    addBillMutate,
    addBillLoading,
    selectedData,
    addElectricityBillMutate,
    addElectricityBillLoading,
    electricityModalOpen,
    setElectricityModalOpen
}) => {

    const user = useSelector((state) => state.UserReducer.user);
    const type = selectedData?.type;
    const [selectedOption, setSelectedOption] = useState(null);
    const [height, setHeight] = useState(window.innerHeight);
    const [roomList, setRoomList] = useState([]);
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [floorsData, setFloorsData] = useState([]);

    const selectOptions = [
        { value: 4, label: "Electricity Bill", icon: FaBolt },
        { value: 12, label: "WiFi Bill", icon: MdOutlineWifi },
        { value: 13, label: "Gas Bill", icon: MdGasMeter },
        { value: 14, label: "Maid Charges", icon: FaBroom },
    ];

    useEffect(() => {
        const handleResize = () => {
            setHeight(window.innerHeight);
        };
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const getSelectedOptionData = () => {
        return selectOptions.find(option => option.value === selectedOption);
    };

    const fetchFloorsData = async () => {
        if (!selectedOption || !selectedData?.id) return;

        const query = `propId=${selectedData?.id}&propType=${selectedData?.type}&utilityType=${selectedOption}`;

        const response = await axios.get(
            `${Config.apiUrl}/extra-charges/list/bill?${query}`,
            {
                headers: {
                    authorization: "Bearer " + user.token,
                    "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8",
                },
            }
        );
        return response.data;
    };

    const {
        isLoading: floorsLoading,
        mutate: fetchFloorsMutate,
        error: floorsError,
        data: floorsResponse,
    } = useMutation(fetchFloorsData, {
        onSuccess: (data) => {
            setFloorsData(data?.data || []);
            if (selectedOption === 4 && data) {
                setElectricityModalOpen(true);
            }
        },
        onError: (error) => {
            setFloorsData([]);
        },
    });

    useEffect(() => {
        if (selectedOption && selectedData?.id) {
            fetchFloorsMutate();
        } else {
            setFloorsData([]);
            setRoomList([]);
            setSelectedFloor(null);
            setSelectedRoom(null);
        }
    }, [selectedOption, selectedData?.id]);

    const handleOptionSelect = (optionValue) => {
        setSelectedOption(optionValue);
        setFloorsData([]);
        setRoomList([]);
        setSelectedFloor(null);
        setSelectedRoom(null);
        setElectricityModalOpen(false);
    };

    const handleFloorSelect = (floor, setFieldValue) => {
        if (floor) {
            setFieldValue("floor", floor.id);
            setSelectedFloor(floor);
            setFieldValue("roomId", "");
            setSelectedRoom(null);

            const selectedFloorData = floorsData?.find(f => f.id.toString() === floor.id.toString());
            setRoomList(selectedFloorData?.rooms || []);
        } else {
            setFieldValue("floor", "");
            setSelectedFloor(null);
            setFieldValue("roomId", "");
            setSelectedRoom(null);
            setRoomList([]);
        }
    };

    const handleSingleRoomSelect = (room, setFieldValue) => {
        if (room) {
            setFieldValue("roomId", room.id);
            setSelectedRoom([room]);
        } else {
            setFieldValue("roomId", "");
            setSelectedRoom(null);
        }
    };

    const handleRoomSelect = (rooms, setFieldValue) => {
        if (rooms && rooms.length > 0) {
            const roomIds = rooms.map(room => room.id);
            setFieldValue("roomId", roomIds.join(","));
            setSelectedRoom(rooms);
        } else {
            setFieldValue("roomId", "");
            setSelectedRoom([]);
        }
    };

    const handleSubmit = (values) => {
        const selectedOptionData = getSelectedOptionData();
        const formData = new FormData();

        formData.append("propId", selectedData?.id);
        formData.append("utilityType", selectedOptionData.value);
        formData.append("startDate", values.startDate);
        formData.append("endDate", values.endDate);
        formData.append("amount", values.amount);
        formData.append("roomFlatId", values.roomId);
        formData.append("description", values.description);

        if (values.file) {
            formData.append("file", values.file[0]);
        }

        addBillMutate(formData);
    };

    const handleBackClick = () => {
        setSelectedFloor(null);
        setSelectedRoom(null);
        setRoomList([]);
        setSelectedOption(null);
        setFloorsData([]);
    };

    const getModalTitle = () => {
        if (!selectedOption) return "Select Utility Bill";

        const selected = getSelectedOptionData();
        return selected ? `Add ${selected.label}` : "Add Utility Bill";
    };

    const handleCloseElectricityModal = () => {
        setElectricityModalOpen(false);
        setSelectedOption(null);
    };

    const handleCloseModal = () => {
        if (selectedOption) {
            setSelectedOption(null);
            setFloorsData([]);
        } else {
            setIsUtilityBill(false);
        }
    };

    return (
        <>
            {!electricityModalOpen &&
                <Container style={{ height: `${height}px`, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>

                    <ModalHeader>
                        <ModalTitle>{getModalTitle()}</ModalTitle>
                        <CloseButton onClick={handleCloseModal}>
                            <MdClose size={24} />
                        </CloseButton>
                    </ModalHeader>

                    <>
                        <UtilityBillHeader
                            selectedData={selectedData}
                            selectedOptionData={getSelectedOptionData()}
                        />
                        <Content>
                            {floorsLoading && (
                                <div className="flex justify-center py-8"><CircularProgress /></div>
                            )}

                            {selectedOption && selectedOption !== 4 && !floorsLoading && floorsResponse && (
                                <UtilityBillForm
                                    type={type}
                                    floorsData={floorsData}
                                    floorsLoading={floorsLoading}
                                    roomList={roomList}
                                    selectedFloor={selectedFloor}
                                    selectedRoom={selectedRoom}
                                    onFloorSelect={handleFloorSelect}
                                    onRoomSelect={(rooms, setFieldValue) => {
                                        if (type === 2) {
                                            handleRoomSelect(rooms, setFieldValue);
                                        } else {
                                            handleSingleRoomSelect(rooms, setFieldValue);
                                        }
                                    }}
                                    onBack={handleBackClick}
                                    onSubmit={handleSubmit}
                                    addBillLoading={addBillLoading}
                                />
                            )}

                            {!selectedOption && !floorsLoading && (
                                <UtilityBillSelectionScreen
                                    selectOptions={selectOptions}
                                    selectedOption={selectedOption}
                                    onOptionSelect={handleOptionSelect}
                                />
                            )}
                        </Content>
                    </>
                </Container>}
            {electricityModalOpen && floorsResponse && (
                <AddElectricityBill
                    data={floorsResponse}
                    propId={selectedData?.id}
                    addBillMutate={addElectricityBillMutate}
                    handleClose={handleCloseElectricityModal}
                    addBillLoading={addElectricityBillLoading}
                    type={type}
                    propertyDetails={selectedData}
                />
            )}
        </>
    );
};

// Styled Components
const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const Header = tw.div`flex items-center justify-between bg-primary-color py-4 px-4`;
const Title = tw.h1`text-white text-lg font-medium`;
const CloseButton = tw.button`text-white`;
const Content = tw.div`px-6 flex flex-col justify-between`;

export default SelectUtilityBill;
